import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Inbox, Pagination, Spinner } from '@vwfs-bronson/bronson-react';
import { LocalizationProp } from '@vwfs-bronson/bronson-react/dist/Inbox';
import { Notification, NotificationStatus } from '@cp-shared-19/frontend-ui';
import { DocumentDownload } from 'cpes-common';
import { formatCpDate } from '@cp-shared-19/common-utilities';
import { CpDataApi } from 'cp-xhr';
import { documentTypeTranslationMapping, PostboxDocumentType } from '../helper/DocumentType';
import { getTableMonthLocalizations } from '../helper/Table';
import { preventDefault } from '../helper/PreventDefault';
import { formatFileSize } from '../helper/FileSize';
import { DocumentDownloadLink } from './types';

export type DocumentsTableRow = {
    type: string;
    date: string;
    categoryCode: number;
    fileName?: string;
    fileSize?: string;
    _links: DocumentDownloadLink;
};

export type DocumentsTableProps<T extends DocumentsTableRow> = {
    documents?: T[];
    isLoading: boolean;
    hasError: boolean;
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    renderDetails: (document: T) => React.ReactNode;
};

const inboxColumns = ['colDate', 'colDownload', 'colSubject'];

const inboxColumnClassNames = {
    colDate: 'u-1/6 u-1/1@s',
    colDownload: 'u-2/12 u-1/1@s',
};

const inboxBodyColumnClassNames = {
    ...inboxColumnClassNames,
    colSubject: 'u-pl-small',
};

type GroupedByYear<T> = { [year: string]: T[] };

const groupByYear = <T extends DocumentsTableRow>(documents: T[]): GroupedByYear<T> => {
    const result: GroupedByYear<T> = {};
    documents.forEach(document => {
        const year = formatCpDate(document.date)
            .toMoment()
            .year()
            .toString();
        result[year] = result[year] ? [...result[year], document] : [document];
    });
    return result;
};

const getSortedYearKeysDescending = <T extends DocumentsTableRow>(grouped: GroupedByYear<T>): string[] =>
    Object.keys(grouped)
        .sort((a, b) => Number.parseInt(a) - Number.parseInt(b))
        .reverse();

export function DocumentsTable<T extends DocumentsTableRow>({
    documents,
    isLoading,
    hasError,
    currentPage,
    totalPages,
    onPageChange,
    renderDetails,
}: DocumentsTableProps<T>): React.ReactElement {
    const { t } = useTranslation('postbox');
    const [downloadingEndpoint, setDownloadingEndpoint] = useState<string | null>(null);
    const [downloadError, setDownloadError] = useState<boolean>(false);

    const documentsVersion = useRef(0);

    useEffect(() => {
        documentsVersion.current += 1;
        setDownloadingEndpoint(null);
        setDownloadError(false);
    }, [documents]);

    const downloadDocument = (endpoint: string): void => {
        const version = documentsVersion.current;
        const isCurrent = (): boolean => version === documentsVersion.current;
        setDownloadingEndpoint(endpoint);
        setDownloadError(false);
        CpDataApi.get(endpoint)
            .then(response => {
                const { file }: DocumentDownload = response.data;
                window.open(file, '_blank');
            })
            .catch(() => isCurrent() && setDownloadError(true))
            .finally(() => isCurrent() && setDownloadingEndpoint(null));
    };

    if (hasError) {
        return (
            <div className="u-mt">
                <Notification status={NotificationStatus.error}>{t('table.errors.api-not-available')}</Notification>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="u-mt">
                <Spinner />
            </div>
        );
    }

    if (!documents || documents.length === 0) {
        return (
            <div className="u-mt">
                <Notification status={NotificationStatus.warning}>
                    {t('table.errors.no-filtered-documents')}
                </Notification>
            </div>
        );
    }

    const groupedDocuments = groupByYear(documents);
    const sortedYearKeysDescending = getSortedYearKeysDescending(groupedDocuments);

    return (
        <>
            <Inbox
                columns={inboxColumns}
                tableHeaderClassNames={inboxColumnClassNames}
                tableColumnClassNames={inboxBodyColumnClassNames}
                tableHeader={[
                    <React.Fragment key="date">{t('table.column-headers.date')}</React.Fragment>,
                    <React.Fragment key="download">{t('table.column-headers.download')}</React.Fragment>,
                    <React.Fragment key="document-type">{t('table.column-headers.document-type')}</React.Fragment>,
                ]}
                localization={(getTableMonthLocalizations(t) as unknown) as LocalizationProp}
                dateFormatter={(day, month, year) =>
                    `${day.padStart(2, '0')}.${(Number(month) + 1).toString().padStart(2, '0')}.${year}`
                }
                sortingOrder="DESC"
                testId={'documents-inbox'}
                className="c-inbox-table u-mb u-mt"
            >
                {sortedYearKeysDescending.map(yearKey => {
                    const group = groupedDocuments[yearKey];
                    return (
                        <Inbox.Section key={yearKey} year={yearKey} showYear showTableHeader>
                            {group.map((document, index) => {
                                const downloadEndpoint = document._links.downloadEndpoint;
                                const typeLabel = t(
                                    `filters.document-type.select-items.${documentTypeTranslationMapping[
                                        document.type as PostboxDocumentType
                                    ] || document.type}`,
                                );
                                const month = formatCpDate(document.date)
                                    .toMoment()
                                    .month()
                                    .toString();
                                const day = formatCpDate(document.date)
                                    .toMoment()
                                    .date()
                                    .toString();
                                const isDownloadingCurrentDocument = downloadingEndpoint === downloadEndpoint;
                                const fileSize = formatFileSize(document.fileSize);

                                if (isDownloadingCurrentDocument) {
                                    return (
                                        <tr key={`${yearKey}-${index}-message`}>
                                            <td colSpan={3}>
                                                <Spinner small />
                                            </td>
                                        </tr>
                                    );
                                }

                                return (
                                    <Inbox.Message
                                        key={`${yearKey}-${index}-message`}
                                        testId={`documentsMessage-${yearKey}-${index}`}
                                        year={yearKey}
                                        month={month}
                                        day={day}
                                        read={true}
                                        download={true}
                                        title={typeLabel}
                                        onDownloadClick={preventDefault(() => downloadDocument(downloadEndpoint))}
                                        onItemClick={preventDefault(() => downloadDocument(downloadEndpoint))}
                                    >
                                        {`(PDF${fileSize ? `, ${fileSize}` : ''}) `}
                                        <strong>{typeLabel}</strong> {renderDetails(document)}
                                    </Inbox.Message>
                                );
                            })}
                        </Inbox.Section>
                    );
                })}
            </Inbox>
            {downloadError && (
                <Notification status={NotificationStatus.error}>{t('table.download-failure-modal.title')}</Notification>
            )}
            {totalPages > 1 && (
                <div className="u-mt u-mb" style={{ display: 'flex', justifyContent: 'center' }}>
                    <Pagination
                        title={t('table.pagination.title')}
                        prevText={t('table.pagination.previous')}
                        nextText={t('table.pagination.next')}
                        prevDisabled={currentPage <= 1}
                        nextDisabled={currentPage >= totalPages}
                        onPrevClick={preventDefault(() => onPageChange(Math.max(1, currentPage - 1)))}
                        onNextClick={preventDefault(() => onPageChange(Math.min(totalPages, currentPage + 1)))}
                        testId={'documents-pagination'}
                    >
                        {Array.from({ length: totalPages }, (_unused, index) => index + 1).map(pageNumber => (
                            <Pagination.Item
                                key={pageNumber}
                                active={pageNumber === currentPage}
                                onClick={preventDefault(() => onPageChange(pageNumber))}
                            >
                                {pageNumber}
                            </Pagination.Item>
                        ))}
                    </Pagination>
                </div>
            )}
        </>
    );
}
